//core
import React, {Dispatch, SetStateAction, useEffect, useMemo, useState} from "react";
import arePropsEqual from "react-fast-compare";
import { useTranslation } from "react-i18next";
import { makeStyles, useTheme } from "@mui/styles";
import { darken, rgbToHex } from "@mui/material/styles";
import { TableCell, Tooltip, Box } from "@mui/material";
import { getSymbolFromCode } from "currency-code-symbol-map";

//theme
import {Theme} from "@mui/material/styles/createTheme";

//types
import { itemColumnsData } from "../../index";
import { OrderType } from "../../../../affiliate/Trackers";

//components
import SortButton from "../SortButton"
import SelectAllRows from "../../../../admin/Reports/Finance_report/components/TableActions/SelectAllRows";
import SelectAllPaysRows from "../../../../admin/Reports/Finance_report/components/Details/components/ReviewAndPay/components/SelectAllPaysRows";
import useEffectOnce from "../../../../../customHooks/useEffectOnce";
import SelectAllDeals from "../../../../admin/Deals/components/TableActions/SelectAllDeals";
import SelectAllAffiliates from "../../../../admin/Partners/components/TableActions/SelectAllAffiliates";
import {useConfiguration} from "../../../../../store/common/configuration/useConfiguration";
import {useSelector} from "react-redux";
import {selectUploadConfigData} from "../../../../../store/common/configuration/selectors";
import {alignRightColumns} from "../../constants";
import {hover} from "@testing-library/user-event/dist/hover";
import SelectAllTrackers
  from "../../../../admin/Partners/PartnerAccount/TrackerList/components/Trackers/components/TableActions/SelectAllTrackers";
import SelectAllWebhooksReport
  from "../../../../admin/Reports/WebhooksReport/components/TableActions/SelectAllWebhooksReport";

const getTooltipTitle = (str: string): any[] =>  {
  const splitted = str.split('\n');

  const result: any[] = [];
  splitted.forEach((element: string, index: number) => {
    result.push(element);
    if (index < splitted.length - 1) {
      result.push(<br/>);
    }
  });

  return result;
}

let useStyles = makeStyles<Theme>((theme) => ({
  tableHeadCell: {
    color: 'inherit',
    whiteSpace: 'nowrap',
    height: 44,
    padding: '3px 16px',
    backgroundColor: rgbToHex(rgbToHex(darken(theme.palette.background.paper, 0.12))),
    borderBottom: 'none',
    position: 'sticky',
    top: 0,
    '&.tableHeadCenter': {
      textAlign: 'center',
    },
    '&:hover': {
      '& button': {
        opacity: 1,
      }
    },
    '&:not(:last-child)': {
      '&:before': {
        content: "''",
        display: 'block',
        position: 'absolute',
        top: '50%',
        right: '-1px',
        transform: 'translateY(-50%)',
        width: 2,
        height: 10,
        backgroundColor: darken(theme.palette.background.paper, 0.25),
      }
    },
    '&.hasActions:nth-last-child(2)::before': {
      display: 'none!important',
    },
    '&.freeze::before': {
      display: 'none!important',
    }
  },
  tableCellWrapper: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: 4,
  },
  stickyHeader: {
    position: 'sticky',
    borderLeft: `1px solid ${rgbToHex(darken(theme.palette.background.paper, 0.12))}`,
    boxShadow: theme.shadows[2],
  },
  stickyActionHeader: {
    right: 0,
  },
  tableCellFreeze: {
    position: 'sticky',
    boxShadow: theme.shadows[2],
    borderLeft: `1px solid ${rgbToHex(darken(theme.palette.background.paper, 0.12))}`,
    zIndex: 5,
    textAlign: 'left',
  },
  sort: {
    marginLeft: '0',
    opacity: 0,
    transition: 'opacity 0s ease 0.175s',
    '&.visible': {
      opacity: '1!important',
    }
  },
}));


interface ITableHeadColumns {
  columns: itemColumnsData[];
  order: OrderType;
  initialOrder: OrderType;
  setOrder: Dispatch<SetStateAction<OrderType>> | Dispatch<OrderType>;
  actions: boolean;
  actionsCount: number;
  disabled?: boolean;
  translationColumnsPrefix: string;
  translationTooltipsPrefix?: string;
  freezeColumns?: string[];
  columnCurrency?: any;
  CollapseComponent?: any;
  key?: any;
  showCurrencyInHeader?: boolean;
}

const TableHeadColumns = (props: ITableHeadColumns) => {
  const theme: Theme = useTheme();
  const styles = useStyles();
  const { t } = useTranslation();
	const { getUploadConf } = useConfiguration();

	const uploadConfigData = useSelector(selectUploadConfigData);

  const {
    columns,
    actions = false,
    actionsCount,
    order,
    initialOrder,
    setOrder,
    disabled = false,
    translationColumnsPrefix,
    freezeColumns,
    translationTooltipsPrefix = '',
    columnCurrency,
    CollapseComponent,
    key = null,
    showCurrencyInHeader = true,
  } = props;

  const [freezeColumn, setFreezeColumn] = useState<number[]>([]);
  const [widthFreezeColumns, setWidthFreezeColumns] = useState<any>(0);

  const count = actionsCount === 1 ? 1.75 : actionsCount;

	useEffect(() => {
		if (!uploadConfigData) {
			getUploadConf();
		}
	}, [uploadConfigData]);

  useEffectOnce(() => {
    if (!!columns && !!freezeColumns) {
      columns.forEach((column) => {
        if (freezeColumns?.includes(column.name)) {
          setFreezeColumn((prevState:any) => {
            return [
              ...prevState,
              document?.getElementById(`${styles.tableCellFreeze + '-' + column.name}`)?.offsetWidth
            ]
          });
        }
      })
    }
  })

  useEffect(() => {
    if (!!freezeColumns && !!freezeColumn) {
      const widthArr = freezeColumn.reduce((acc:any, item:number, index:number) => {
        if (freezeColumn.length - 1 !== index) {
          const width = acc[index] + item;
          acc.push(width);
        }
        return acc;
      },[0]);

      setWidthFreezeColumns(widthArr);
    }
  },[freezeColumn])

  useEffect(() => {
    return () => {
      setWidthFreezeColumns(0);
      setFreezeColumn([]);
    }
  },[])

  const getColumnTitle = (item: itemColumnsData) => {
    if (!showCurrencyInHeader) {
      return t(`${translationColumnsPrefix}${item.name}`);
    }

    return (
      <>
        {item.hasOwnProperty('isRecalculatable') && item.isRecalculatable ? (
          <>
            {t(`${translationColumnsPrefix}${item.name}`).includes('{currency}')
              ? t(`${translationColumnsPrefix}${item.name}`).replace('{currency}', (!!columnCurrency && (!!getSymbolFromCode(columnCurrency.id.toUpperCase()) ? getSymbolFromCode(columnCurrency.id.toUpperCase()) : columnCurrency.id)) || '$')
              : t(`${translationColumnsPrefix}${item.name}`).includes('$')
                ? t(`${translationColumnsPrefix}${item.name}`).replace('$', (!!columnCurrency && (!!getSymbolFromCode(columnCurrency.id.toUpperCase()) ? getSymbolFromCode(columnCurrency.id.toUpperCase()) : columnCurrency.id)) || '$')
                : `${t(`${translationColumnsPrefix}${item.name}`)}, ${!!columnCurrency && (!!getSymbolFromCode(columnCurrency.id.toUpperCase()) ? getSymbolFromCode(columnCurrency.id.toUpperCase()) : columnCurrency.id) || '$'}`}
          </>
        ) : <>
	        {t(`${translationColumnsPrefix}${item.name}`).includes('{currency}')
		        ? t(`${translationColumnsPrefix}${item.name}`).replace('{currency}', !!uploadConfigData ? getSymbolFromCode(uploadConfigData.defaultCurrency.toUpperCase()) || '$' : '$')
            : t(`${translationColumnsPrefix}${item.name}`)}
        </>}
      </>
    )
  }

  return (
    <>
      {
        !!CollapseComponent && (
          <TableCell
            className={`${styles.tableHeadCell}`}
            align="left"
            id={`table-head-collapse`}
            style={{ width: '66px' }}
            >
          </TableCell>
        )
      }
      {
        columns.map((item,index) => {
          const columnName = !!translationTooltipsPrefix ? <>
            {!!t(`${translationTooltipsPrefix}${item.name}`) ? (
              <Tooltip
                title={
                  getTooltipTitle(t(`${translationTooltipsPrefix}${item.name}`))
                }
                placement="bottom"
              >
                <span>{getColumnTitle(item)}</span>
              </Tooltip>
            ) : <>{getColumnTitle(item)}</>}
          </> : <>{getColumnTitle(item)}</>;

          const isFreeze = freezeColumns?.includes(item.name);

          return <TableCell
            className={`${styles.tableHeadCell} ${actionsCount >= 1 ? 'hasActions' : ''} ${freezeColumns?.includes(item.name) ? `${styles.tableCellFreeze}` : ''} ${isFreeze ? 'freeze' : ''}`}
            key={`${item.name}-${key}`}
            id={`${freezeColumns?.includes(item.name) ? `${styles.tableCellFreeze + '-' + item.name}` : ''}`}
            {...(freezeColumns ? {sx: {
                left: `${freezeColumns?.includes(item.name) && !!freezeColumn ? `${widthFreezeColumns[index]}px` : `${widthFreezeColumns[widthFreezeColumns.length - 1]}px`}`
              }} : {})}
            align={alignRightColumns.includes(item.type) ? 'right' : 'left'}
          >
            {item.type === 'finAffId' && <SelectAllRows/> }
            {item.type === 'finReviewPayAffId' && <SelectAllPaysRows/> }
            {item.type === 'dealsSelectBrand' && <SelectAllDeals /> }
            {item.type === 'partnersSelectRow' && <SelectAllAffiliates /> }
            {item.type === 'trackersDealsSelectRow' && <SelectAllTrackers archived={item.archived} /> }
            {item.type === 'webhooksReportSelectRow' && <SelectAllWebhooksReport /> }
            <Box
              component="span"
              className={styles.tableCellWrapper}
              sx={{ flexDirection: alignRightColumns.includes(item.type) ? "row-reverse" : "" }}
            >
              {columnName}
              {
                item.sort && (
                  <SortButton
                    className={`${styles.sort} ${Object.keys(order)[0] === item.name ? 'visible' : ''} `}
                    order={order}
                    setOrder={setOrder}
                    name={item.name}
                    initialOrder={initialOrder}
                  />
                )
              }
            </Box>
          </TableCell>
        })
      }
      { actions && <TableCell
        className={`
          ${styles.tableHeadCell} 
          ${styles.stickyHeader} 
          ${styles.stickyActionHeader}
          ${actionsCount === 1 ? 'tableHeadCenter' : ''}
        `}
        align="left"
        sx={{
          minWidth: `${32 + 36*(count)}px`,
          maxWidth: `${32 + 36*count}px`,
          width: `${32 + 36*count}px`,
          backgroundColor: disabled
            ? rgbToHex(darken(theme.palette.background.paper, 0.04))
            : rgbToHex(darken(theme.palette.background.paper, 0.09))
        }}
      >
        {t("columns_for_display.table.actions")}
      </TableCell> }
    </>
  );
};

export default React.memo(TableHeadColumns, arePropsEqual);
